import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Flex } from 'antd';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreateWidgetSettingsWidgetSettingsCreatePostMutation,
  useGetWidgetSettingsInfoWidgetSettingsInfoGetQuery,
  useUpdateWidgetSettingsWidgetSettingsUpdatePostMutation,
} from '@shared/api';
import { useNotify } from '@shared/hooks/use-notify';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { PageHeader } from '@shared/ui/page-header';
import { Text } from '@shared/ui/antd';
import { usePresetFormSchema } from '../../hooks/use-preset-form-schema';
import { useUnsavedChangesGuard } from '../../hooks/use-unsaved-changes-guard';
import { PRESET_FORM_ID } from '../../lib/constants';
import { getPresetFormValues, normalizePresetValues } from '../../lib/default-values';
import type { PresetFormValues } from '../../lib/types';
import { resolveWidgetLang } from '../../lib/widget-translations';
import { MetrikaGuideModal } from '../metrika-guide-modal/metrika-guide-modal';
import { PresetForm } from '../preset-form/preset-form';
import { PresetWidgetPreview } from '../widget-preview/preset-widget-preview';
import { UnsavedChangesModal } from '../unsaved-changes-modal/unsaved-changes-modal';
import styles from './preset-editor.module.scss';

export const PresetEditor = () => {
  const { presetId } = useParams<{ presetId: string }>();
  const { t, i18n } = useTranslation('translation', { keyPrefix: 'presets.editor' });
  const widgetLang = resolveWidgetLang(i18n.resolvedLanguage ?? i18n.language);
  const navigate = useNavigate();
  const notify = useNotify();
  const organizationId = useAppSelector(authSelectors.selectCurrentOrganizationId);
  const schema = usePresetFormSchema();
  const numericPresetId = Number(presetId);
  const isEditing = Boolean(presetId);
  const hasValidPresetId = Number.isInteger(numericPresetId) && numericPresetId > 0;
  const [createdPresetId, setCreatedPresetId] = useState<number | null>(null);
  const [isMetrikaGuideOpen, setIsMetrikaGuideOpen] = useState(false);

  const form = useForm<PresetFormValues>({
    resolver: zodResolver(schema),
    defaultValues: getPresetFormValues(undefined, widgetLang),
    mode: 'onChange',
  });
  const {
    reset,
    formState: { isDirty, isValid },
  } = form;
  const previewValues = useWatch({ control: form.control }) as PresetFormValues;

  const presetQuery = useGetWidgetSettingsInfoWidgetSettingsInfoGetQuery(
    { widgetSettingsId: hasValidPresetId ? numericPresetId : 0 },
    { skip: !isEditing || !hasValidPresetId },
  );
  const [createPreset, { isLoading: isCreating }] =
    useCreateWidgetSettingsWidgetSettingsCreatePostMutation();
  const [updatePreset, { isLoading: isUpdating }] =
    useUpdateWidgetSettingsWidgetSettingsUpdatePostMutation();
  const isSaving = isCreating || isUpdating;
  const isPresetLoading = isEditing && presetQuery.isLoading;

  const { isLeaveConfirmationOpen, confirmLeave, cancelLeave } = useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    if (!presetQuery.data || isDirty) return;
    reset(getPresetFormValues(presetQuery.data));
  }, [isDirty, presetQuery.data, reset]);

  useEffect(() => {
    if (isEditing) return;
    reset(getPresetFormValues(undefined, widgetLang), { keepDirtyValues: true });
  }, [isEditing, reset, widgetLang]);

  useEffect(() => {
    if (!createdPresetId) return;
    navigate(`../${createdPresetId}`, { replace: true });
  }, [createdPresetId, navigate]);

  const handleBack = () => navigate('..');

  const handleSubmit = async (values: PresetFormValues) => {
    if (!organizationId) {
      notify({ message: t('notifications.organizationMissing') });
      return;
    }

    const normalizedValues = normalizePresetValues(values);

    try {
      if (isEditing && hasValidPresetId) {
        const updatedPreset = await updatePreset({
          widgetSettingsId: numericPresetId,
          baseWidgetSettings: normalizedValues,
        }).unwrap();
        reset(getPresetFormValues(updatedPreset));
        notify({ type: 'success', message: t('notifications.updated') });
        return;
      }

      const createdPreset = await createPreset({
        organizationId,
        baseWidgetSettings: normalizedValues,
      }).unwrap();
      reset(getPresetFormValues(createdPreset));
      notify({ type: 'success', message: t('notifications.created') });
      setCreatedPresetId(createdPreset.id);
    } catch {
      notify({ message: t('notifications.saveError') });
    }
  };

  return (
    <Flex vertical gap={20} className={styles.root}>
      <PageHeader
        title={t(isEditing ? 'editTitle' : 'createTitle')}
        backButtonLabel={t('back')}
        onBack={handleBack}
        extra={
          <Button
            type="primary"
            htmlType="submit"
            form={PRESET_FORM_ID}
            loading={isSaving}
            disabled={isSaving || !isDirty || !isValid || isPresetLoading}
          >
            {t('save')}
          </Button>
        }
      />

      <div className={styles.workspace}>
        <div className={styles.settings}>
          {isPresetLoading && <LoadingState />}
          {isEditing && !hasValidPresetId && (
            <Alert type="error" showIcon message={t('invalidPresetId')} />
          )}
          {!isPresetLoading && presetQuery.isError && (
            <FetchingErrorState refetch={presetQuery.refetch} />
          )}
          {!isPresetLoading && !presetQuery.isError && (!isEditing || hasValidPresetId) && (
            <PresetForm
              form={form}
              disabled={isSaving}
              onSubmit={handleSubmit}
              onOpenMetrikaGuide={() => setIsMetrikaGuideOpen(true)}
            />
          )}
        </div>

        <Flex vertical gap={8} className={styles.preview}>
          <Text variant="lg" fontWeight={500}>
            {t('preview.title')}
          </Text>
          <Text variant="sm" className={styles.description}>
            {t('preview.description')}
          </Text>
          <PresetWidgetPreview settings={previewValues} />
        </Flex>
      </div>

      <UnsavedChangesModal
        isOpen={isLeaveConfirmationOpen}
        onLeave={confirmLeave}
        onStay={cancelLeave}
      />

      <MetrikaGuideModal isOpen={isMetrikaGuideOpen} onClose={() => setIsMetrikaGuideOpen(false)} />
    </Flex>
  );
};
